import { useRef } from 'react';
import { Pressable, View } from 'react-native';

import { Button, HStack, Text, VStack, theme } from '@squire/ui';

import { CloseIcon, InfoIcon } from '@/components/_icons';
import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { PromoCodeModal } from '@/components/_shared/PromoCodeModal';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { formatPrice } from '@/utils/internationalizationUtils';
import { getMembershipDiscount } from '@/utils/membershipUtils';
import {
  getDiscountAmount,
  getGiftCardAmount,
  getTaxesAndFeesAmount,
} from '@/utils/orderBreakdown';
import { PaymentMethodType } from '@/utils/paymentMethods';

import { ConfirmBookingTaxesAndFeesBottomSheet } from '../ConfirmBookingTaxesAndFeesBottomSheet';
import { styles } from './styles';
import type { ConfirmBookingSummaryProps, SummaryItem } from './types';

const ConfirmBookingSummary = ({
  shop,
  cart,
  breakdown,
  tipsTotal,
  isLoadingCart,
  onSetPromoCode,
  selectedPaymentMethod,
  currency = 'USD',
  removeGiftCardCode,
  isWaitlistMode = false,
  isUpdatingBreakdown = false,
  validatedCart = null,
}: ConfirmBookingSummaryProps) => {
  const { t, i18n } = useScreenTranslations('ConfirmBookingBottomSheet');

  const taxesBottomSheetRef = useRef<BottomSheetRef>(null);
  const promoCodeBottomSheetRef = useRef<BottomSheetRef>(null);

  // When a guest's details have been validated, the API's own cart is the
  // source of truth: it already applies the membership discount, re-sizes
  // gift-card payments and recomputes the total (SQR-15069).
  const activeBreakdown = validatedCart?.breakdown ?? breakdown;
  const activeAdjustments = validatedCart?.adjustments ?? cart?.adjustments;
  const activePayments = validatedCart?.payments ?? cart?.payments;

  const servicesTotal = activeBreakdown?.breakdownPrice || 0;

  const isCartWithPromo = !!cart?.promo || !!cart?.promoCode;

  // The promo guard below asks whether a gift card is on the *cart* (not the
  // validated/re-sized payments), since that's what the input's promo-vs-gift-card
  // exclusivity is keyed on.
  const hasGiftCardOnCart = cart?.payments?.some(
    (payment) => payment.type === 'gift_card',
  );

  const isPayingInPerson =
    selectedPaymentMethod?.type === PaymentMethodType.payInPerson;

  const feeApplies =
    isWaitlistMode || (!!selectedPaymentMethod && !isPayingInPerson);
  const feeAmount = feeApplies ? activeBreakdown?.totalFee || 0 : 0;

  const membershipDiscount = getMembershipDiscount(activeAdjustments);

  // SQR-15545: a card payment attached before checkout is not charged yet, so
  // add it back to the total. Gift cards are redemptions and stay subtracted.
  const pendingCardPaymentsTotal =
    activePayments
      ?.filter((payment) => payment.type !== 'gift_card')
      .reduce((sum, payment) => sum + (payment.amount || 0), 0) ?? 0;

  // Strip the fee (and tip, when paying in person) that outstandingChargeAmount
  // bakes in but that this selection shouldn't be charged for.
  const displayedTotal =
    (activeBreakdown?.outstandingChargeAmount || 0) +
    pendingCardPaymentsTotal -
    (feeApplies ? 0 : activeBreakdown?.totalFee || 0) -
    (isPayingInPerson ? tipsTotal : 0);

  const canAddPromoCode =
    !isPayingInPerson &&
    !!onSetPromoCode &&
    (!isCartWithPromo || !hasGiftCardOnCart);

  const getSummaryItems = () => {
    // Single gate for the whole summary: without a breakdown there is no live
    // cart to summarize (e.g. after tapping Book, cart.breakdown clears while the
    // cart object — with adjustments/payments — briefly lingers in cache). Every
    // line below reads either the breakdown or the cart, so we bail out here once
    // instead of re-checking `breakdown` per line. This keeps a stray discount
    // line from surviving alone under a $0 total.
    if (!activeBreakdown) return [];

    // Grouped rather than pushed in sequence so the reading order is guaranteed:
    // what is owed, then what reduces it, then what pays it down.
    const charges: SummaryItem[] = [];
    const discounts: SummaryItem[] = [];
    const redemptions: SummaryItem[] = [];

    if (servicesTotal > 0) {
      charges.push({
        label: t('services'),
        value: servicesTotal,
      });
    }

    if (
      tipsTotal > 0 &&
      selectedPaymentMethod?.type !== PaymentMethodType.payInPerson
    ) {
      charges.push({
        label: t('tips'),
        value: tipsTotal,
      });
    }

    const taxesAndFeesAmount = getTaxesAndFeesAmount(
      activeBreakdown,
      feeAmount,
    );
    if (taxesAndFeesAmount !== null) {
      charges.push({
        label: t('taxes'),
        value: taxesAndFeesAmount,
        onPressTooltip: () => taxesBottomSheetRef.current?.open(),
      });
    }

    if (isWaitlistMode && !!activeBreakdown.waitingListFee) {
      charges.push({
        label: t('waitlistFeeLabel'),
        value: activeBreakdown.waitingListFee,
      });
    }

    const discountAmount = getDiscountAmount(activeBreakdown);
    if (discountAmount !== null) {
      discounts.push({
        label: t('promoCode'),
        color: 'textPositive',
        value: discountAmount,
        tooltipIcon: (props) => <CloseIcon {...props} />,
        onPressTooltip: () => onSetPromoCode?.(null),
      });
    }

    if (membershipDiscount > 0) {
      discounts.push({
        label: t('membership'),
        color: 'textPositive',
        value: -membershipDiscount,
      });
    }

    const giftCardAmount = getGiftCardAmount(activePayments);
    if (giftCardAmount !== null) {
      redemptions.push({
        label: t('giftCard'),
        color: 'textPositive',
        value: giftCardAmount,
        tooltipIcon: (props) => <CloseIcon {...props} />,
        onPressTooltip: () => removeGiftCardCode?.(),
      });
    }

    return [...charges, ...discounts, ...redemptions];
  };

  const handleOpenPromoCodeModal = () => {
    promoCodeBottomSheetRef.current?.open();
  };

  const handleApplyPromoCode = (code: string | null) => {
    onSetPromoCode?.(code);
  };

  return (
    <>
      <VStack space={theme.spacing[3]}>
        <HStack style={styles.summaryTitleRow}>
          <Text variant="lStrong" color="textPrimary">
            {t('summary')}
          </Text>

          {canAddPromoCode && (
            <Button
              size="small"
              variant="textLink"
              style={styles.promoButton}
              onPress={handleOpenPromoCodeModal}
            >
              {t('addPromoCode')}
            </Button>
          )}
        </HStack>

        <VStack space={theme.spacing[2]}>
          {getSummaryItems().map(
            ({ tooltipIcon: TooltipIcon = InfoIcon, ...item }) => (
              <HStack style={styles.summaryRow} key={item.label}>
                <Pressable
                  disabled={!item.onPressTooltip}
                  onPress={item.onPressTooltip}
                >
                  <HStack>
                    <Text variant="m" color="textSecondary">
                      {item.label}{' '}
                    </Text>
                    {item.onPressTooltip && (
                      <View style={styles.tooltipIconContainer}>
                        <TooltipIcon size={12} color="iconSecondary" />
                      </View>
                    )}
                  </HStack>
                </Pressable>

                <Text
                  variant="m"
                  style={[
                    styles.summaryValue,
                    isUpdatingBreakdown && styles.updatingAmount,
                  ]}
                  color={item.color || 'textSecondary'}
                >
                  {item.value < 0 ? '- ' : ''}
                  {formatPrice(Math.abs(item.value / 100), {
                    locale: i18n.language,
                    currency,
                  })}
                </Text>
              </HStack>
            ),
          )}
        </VStack>

        <HStack style={styles.totalRow}>
          <Text variant="mStrong">{t('total')}</Text>
          <Text
            variant="mStrong"
            style={[
              styles.summaryValue,
              isUpdatingBreakdown && styles.updatingAmount,
            ]}
          >
            {formatPrice(displayedTotal / 100, {
              locale: i18n.language,
              currency,
            })}
          </Text>
        </HStack>
      </VStack>

      <ConfirmBookingTaxesAndFeesBottomSheet
        ref={taxesBottomSheetRef}
        breakdown={activeBreakdown}
        brandSettings={shop?.brand?.settings}
        currency={currency}
        isWaitlistMode={isWaitlistMode}
        feeApplies={feeApplies}
      />

      <PromoCodeModal
        isLoading={!!isLoadingCart}
        ref={promoCodeBottomSheetRef}
        onApply={handleApplyPromoCode}
      />
    </>
  );
};

ConfirmBookingSummary.displayName = 'ConfirmBookingSummary';
export { ConfirmBookingSummary };
